import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  CheckCircle, XCircle, Clock, Loader2, 
  Users, UserPlus, Building2, RefreshCw,
  ChevronDown, ChevronUp, ExternalLink, Lock, Mail, Download,
  Ticket, Link2, DollarSign, Crown, Copy
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PasswordGate = ({ onSuccess, setStoredPassword }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    
    try {
      await axios.post(`${API}/admin/verify`, { password });
      sessionStorage.setItem("admin_auth", "true");
      sessionStorage.setItem("admin_pw", password);
      setStoredPassword(password);
      onSuccess();
    } catch (err) {
      setError(true);
      setTimeout(() => setError(false), 2000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-parchment min-h-screen flex items-center justify-center px-6">
      <motion.div 
        className="max-w-md w-full text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="w-16 h-16 rounded-full border border-stone-300 flex items-center justify-center mx-auto mb-8">
          <Lock className="w-6 h-6 text-stone-500" />
        </div>
        <h1 className="font-cormorant text-3xl text-charcoal mb-2">Admin Access</h1>
        <p className="font-manrope text-sm text-stone-500 mb-8">Enter password to continue</p>
        
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className={`w-full px-4 py-4 border ${error ? 'border-red-400' : 'border-stone-300'} bg-white text-center font-manrope tracking-widest focus:outline-none focus:border-gold-500 transition-colors`}
            autoFocus
            disabled={loading}
          />
          {error && (
            <p className="font-manrope text-sm text-red-500 mt-2">Invalid password</p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full mt-4 bg-charcoal text-parchment py-4 font-manrope text-xs tracking-[0.15em] uppercase hover:bg-gold-600 transition-colors disabled:opacity-50"
          >
            {loading ? "Verifying..." : "Enter"}
          </button>
        </form>
      </motion.div>
    </main>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-blue-50 text-blue-700 border-blue-200",
    accepted: "bg-green-50 text-green-700 border-green-200",
    declined: "bg-red-50 text-red-700 border-red-200"
  };
  
  return (
    <span className={`px-3 py-1 text-xs font-manrope uppercase tracking-wider border ${styles[status] || styles.pending}`}>
      {status || "pending"}
    </span>
  );
};

const RequestCard = ({ request, type, onApprove, onDecline, loading }) => {
  const [expanded, setExpanded] = useState(false);
  const isPending = !request.status || request.status === "pending";
  
  const getName = () => {
    if (type === "nomination") return request.nominee_name;
    return request.full_name;
  };
  
  const getEmail = () => {
    if (type === "nomination") return request.nominee_email;
    return request.email;
  };

  return (
    <motion.div 
      className="border border-stone-200 bg-white"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div 
        className="p-5 flex items-center justify-between cursor-pointer hover:bg-stone-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <div>
            <p className="font-manrope text-charcoal font-medium">{getName()}</p>
            <p className="font-manrope text-xs text-stone-500">{getEmail()}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <StatusBadge status={request.status} />
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-stone-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-stone-400" />
          )}
        </div>
      </div>
      
      {expanded && (
        <div className="border-t border-stone-100 p-5 space-y-4 bg-stone-50">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            {type === "invitation" && (
              <>
                <div>
                  <p className="font-manrope text-[10px] text-stone-400 uppercase tracking-wider mb-1">Role</p>
                  <p className="text-charcoal">{request.primary_role}</p>
                </div>
                <div>
                  <p className="font-manrope text-[10px] text-stone-400 uppercase tracking-wider mb-1">Market</p>
                  <p className="text-charcoal">{request.primary_market}</p>
                </div>
                <div>
                  <p className="font-manrope text-[10px] text-stone-400 uppercase tracking-wider mb-1">Volume</p>
                  <p className="text-charcoal">{request.annual_volume}</p>
                </div>
                {request.phone && (
                  <div>
                    <p className="font-manrope text-[10px] text-stone-400 uppercase tracking-wider mb-1">Phone</p>
                    <p className="text-charcoal">{request.phone}</p>
                  </div>
                )}
                {request.verification_link && (
                  <div className="col-span-2">
                    <p className="font-manrope text-[10px] text-stone-400 uppercase tracking-wider mb-1">Verification</p>
                    <a href={request.verification_link} target="_blank" rel="noopener noreferrer" className="text-gold-600 hover:text-gold-700 flex items-center gap-1">
                      {request.verification_link.substring(0, 40)}...
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
                {request.future_interests && request.future_interests.length > 0 && (
                  <div className="col-span-3">
                    <p className="font-manrope text-[10px] text-stone-400 uppercase tracking-wider mb-1">Future Interests</p>
                    <div className="flex flex-wrap gap-2">
                      {request.future_interests.map((interest, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gold-50 text-gold-700 border border-gold-200 text-xs">
                          {interest}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            
            {type === "nomination" && (
              <>
                <div>
                  <p className="font-manrope text-[10px] text-stone-400 uppercase tracking-wider mb-1">Nominated By</p>
                  <p className="text-charcoal">{request.nominator_name}</p>
                </div>
                <div>
                  <p className="font-manrope text-[10px] text-stone-400 uppercase tracking-wider mb-1">Nominator Email</p>
                  <p className="text-charcoal">{request.nominator_email}</p>
                </div>
                <div>
                  <p className="font-manrope text-[10px] text-stone-400 uppercase tracking-wider mb-1">Nominee Role</p>
                  <p className="text-charcoal">{request.nominee_role}</p>
                </div>
                <div className="col-span-2">
                  <p className="font-manrope text-[10px] text-stone-400 uppercase tracking-wider mb-1">Relationship</p>
                  <p className="text-charcoal">{request.relationship}</p>
                </div>
              </>
            )}
            
            {type === "sponsor" && (
              <>
                <div>
                  <p className="font-manrope text-[10px] text-stone-400 uppercase tracking-wider mb-1">Company</p>
                  <p className="text-charcoal">{request.company}</p>
                </div>
                <div>
                  <p className="font-manrope text-[10px] text-stone-400 uppercase tracking-wider mb-1">Category</p>
                  <p className="text-charcoal">{request.category}</p>
                </div>
              </>
            )}
          </div>
          
          {(request.why_room || request.why_nominee || request.note) && (
            <div className="pt-4 border-t border-stone-200">
              <p className="font-manrope text-[10px] text-stone-400 uppercase tracking-wider mb-2">
                {type === "nomination" ? "Why This Nominee" : type === "sponsor" ? "Note" : "Why The Room"}
              </p>
              <p className="text-stone-600 text-sm leading-relaxed">
                {request.why_room || request.why_nominee || request.note}
              </p>
            </div>
          )}
          
          <div className="pt-4 border-t border-stone-200 flex flex-wrap gap-4 text-xs text-stone-400">
            <span>Submitted: {new Date(request.created_at).toLocaleDateString()}</span>
            {request.approved_at && <span>Approved: {new Date(request.approved_at).toLocaleDateString()}</span>}
            {request.accepted_at && <span>Accepted: {new Date(request.accepted_at).toLocaleDateString()}</span>}
          </div>
          
          {isPending && (
            <div className="pt-4 border-t border-stone-200 flex gap-3">
              <button
                onClick={() => onApprove(request.id)}
                disabled={loading}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 font-manrope text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Approve
              </button>
              <button
                onClick={() => onDecline(request.id)}
                disabled={loading}
                className="flex-1 bg-white hover:bg-stone-100 border border-stone-300 text-stone-600 py-3 font-manrope text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                Decline
              </button>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

const AdminDashboard = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [storedPassword, setStoredPassword] = useState("");
  const [activeTab, setActiveTab] = useState("invitations");
  const [invitations, setInvitations] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [attendeeStats, setAttendeeStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [accessLinks, setAccessLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [filter, setFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [showCreateLink, setShowCreateLink] = useState(false);
  const [newLinkData, setNewLinkData] = useState({ full_name: "", email: "", note: "" });
  const [createdLink, setCreatedLink] = useState(null);

  useEffect(() => {
    const isAuth = sessionStorage.getItem("admin_auth") === "true";
    const savedPw = sessionStorage.getItem("admin_pw") || "";
    setAuthenticated(isAuth);
    setStoredPassword(savedPw);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, nomRes, sponRes, waitRes, attendeeRes, partnerRes, linksRes] = await Promise.all([
        axios.get(`${API}/invitation-requests`),
        axios.get(`${API}/nomination-requests`),
        axios.get(`${API}/sponsor-requests`),
        axios.get(`${API}/waitlist`),
        axios.get(`${API}/admin/attendees`),
        axios.get(`${API}/admin/partners`),
        axios.get(`${API}/admin/access-links`)
      ]);
      setInvitations(invRes.data.requests || []);
      setNominations(nomRes.data.requests || []);
      setSponsors(sponRes.data.requests || []);
      setWaitlist(waitRes.data.signups || []);
      setAttendees(attendeeRes.data.attendees || []);
      setAttendeeStats(attendeeRes.data.stats || null);
      setPartners(partnerRes.data.inquiries || []);
      setAccessLinks(linksRes.data.links || []);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authenticated) {
      fetchData();
    }
  }, [authenticated]);

  const handleApprove = async (id, type) => {
    setActionLoading(id);
    try {
      const endpoint = type === "nomination" 
        ? `${API}/admin/approve-nomination/${id}`
        : `${API}/admin/approve-invitation/${id}`;
      await axios.post(endpoint);
      await fetchData();
    } catch (err) {
      console.error("Approval failed:", err);
      alert(err.response?.data?.detail || "Approval failed");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (id, type) => {
    setActionLoading(id);
    try {
      await axios.post(`${API}/admin/decline/${type}/${id}`);
      await fetchData();
    } catch (err) {
      console.error("Decline failed:", err);
      alert(err.response?.data?.detail || "Decline failed");
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_auth");
    sessionStorage.removeItem("admin_pw");
    setAuthenticated(false);
    setStoredPassword("");
  };

  const handleExport = async (collectionType) => {
    if (!storedPassword) {
      alert("Please re-login to export data");
      handleLogout();
      return;
    }
    
    setExporting(true);
    try {
      const response = await fetch(`${API}/admin/export/${collectionType}?password=${encodeURIComponent(storedPassword)}`);
      if (!response.ok) {
        throw new Error("Export failed");
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${collectionType}_export.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleCreateAccessLink = async () => {
    if (!newLinkData.full_name || !newLinkData.email) {
      alert("Please enter name and email");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/create-access-link?password=${encodeURIComponent(storedPassword)}`,
        newLinkData
      );
      setCreatedLink(response.data);
      setNewLinkData({ full_name: "", email: "", note: "" });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create link");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const filterRequests = (requests) => {
    if (filter === "all") return requests;
    return requests.filter(r => (r.status || "pending") === filter);
  };

  const getStats = (requests) => {
    const pending = requests.filter(r => !r.status || r.status === "pending").length;
    const approved = requests.filter(r => r.status === "approved").length;
    const accepted = requests.filter(r => r.status === "accepted").length;
    const declined = requests.filter(r => r.status === "declined").length;
    return { pending, approved, accepted, declined, total: requests.length };
  };

  if (!authenticated) {
    return <PasswordGate onSuccess={() => setAuthenticated(true)} setStoredPassword={setStoredPassword} />;
  }

  const invStats = getStats(invitations);
  const nomStats = getStats(nominations);
  const sponStats = getStats(sponsors);

  return (
    <main className="bg-parchment min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-stone-200 py-6 bg-white">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div>
            <p className="font-manrope text-[10px] tracking-[0.2em] text-stone-400 uppercase">Admin</p>
            <h1 className="font-cormorant text-2xl text-charcoal">The Room Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-stone-200 text-stone-500 hover:text-charcoal hover:border-stone-300 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="font-manrope text-xs">Refresh</span>
            </button>
            <button
              onClick={handleLogout}
              className="font-manrope text-xs text-stone-400 hover:text-charcoal transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="p-5 border border-stone-200 bg-white">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-4 h-4 text-amber-500" />
              <span className="font-manrope text-xs text-stone-400">Pending</span>
            </div>
            <p className="font-cormorant text-3xl text-charcoal">
              {invStats.pending + nomStats.pending + sponStats.pending}
            </p>
          </div>
          <div className="p-5 border border-stone-200 bg-white">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="font-manrope text-xs text-stone-400">Accepted</span>
            </div>
            <p className="font-cormorant text-3xl text-charcoal">
              {invStats.accepted + nomStats.accepted}
            </p>
          </div>
          <div className="p-5 border border-stone-200 bg-white">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-4 h-4 text-gold-500" />
              <span className="font-manrope text-xs text-stone-400">Total Requests</span>
            </div>
            <p className="font-cormorant text-3xl text-charcoal">
              {invStats.total + nomStats.total + sponStats.total}
            </p>
          </div>
          <div className="p-5 border border-stone-200 bg-white">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-4 h-4 text-blue-500" />
              <span className="font-manrope text-xs text-stone-400">Sponsors</span>
            </div>
            <p className="font-cormorant text-3xl text-charcoal">{sponStats.total}</p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6 border-b border-stone-200 bg-white overflow-x-auto">
          <div className="flex gap-1">
            {[
              { id: "attendees", label: "Attendees", count: attendees.length, icon: Ticket },
              { id: "invitations", label: "Invitations", count: invStats.total, icon: Users },
              { id: "nominations", label: "Nominations", count: nomStats.total, icon: UserPlus },
              { id: "partners", label: "Partners", count: partners.length, icon: Building2 },
              { id: "access-links", label: "Direct Links", count: accessLinks.length, icon: Link2 },
              { id: "waitlist", label: "Waitlist", count: waitlist.length, icon: Mail }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-4 font-manrope text-xs tracking-wider transition-colors relative ${
                  activeTab === tab.id ? "text-charcoal" : "text-stone-400 hover:text-stone-600"
                }`}
              >
                <span className="flex items-center gap-2">
                  <tab.icon className="w-4 h-4" />
                  {tab.label} ({tab.count})
                </span>
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold-500" />
                )}
              </button>
            ))}
          </div>
          <button
            onClick={() => handleExport(activeTab)}
            disabled={exporting}
            className="mr-4 flex items-center gap-2 px-4 py-2 border border-stone-200 text-stone-500 hover:text-charcoal hover:border-stone-300 transition-colors disabled:opacity-50"
            data-testid="export-csv-btn"
          >
            <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
            <span className="font-manrope text-xs">{exporting ? 'Exporting...' : 'Export CSV'}</span>
          </button>
        </div>

        <div className="flex gap-2 mb-6">
          {["all", "pending", "approved", "accepted", "declined"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 font-manrope text-[10px] uppercase tracking-wider border transition-colors ${
                filter === f 
                  ? "bg-charcoal text-white border-charcoal" 
                  : "text-stone-500 border-stone-200 hover:border-stone-300 bg-white"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {activeTab === "invitations" && (
              filterRequests(invitations).length === 0 ? (
                <p className="text-center text-stone-400 py-12 font-manrope">No invitation requests found</p>
              ) : (
                filterRequests(invitations).map((req) => (
                  <RequestCard
                    key={req.id}
                    request={req}
                    type="invitation"
                    onApprove={(id) => handleApprove(id, "invitation")}
                    onDecline={(id) => handleDecline(id, "invitation")}
                    loading={actionLoading === req.id}
                  />
                ))
              )
            )}
            
            {activeTab === "nominations" && (
              filterRequests(nominations).length === 0 ? (
                <p className="text-center text-stone-400 py-12 font-manrope">No nominations found</p>
              ) : (
                filterRequests(nominations).map((req) => (
                  <RequestCard
                    key={req.id}
                    request={req}
                    type="nomination"
                    onApprove={(id) => handleApprove(id, "nomination")}
                    onDecline={(id) => handleDecline(id, "nomination")}
                    loading={actionLoading === req.id}
                  />
                ))
              )
            )}
            
            {activeTab === "sponsors" && (
              filterRequests(sponsors).length === 0 ? (
                <p className="text-center text-stone-400 py-12 font-manrope">No sponsor requests found</p>
              ) : (
                filterRequests(sponsors).map((req) => (
                  <RequestCard
                    key={req.id}
                    request={req}
                    type="sponsor"
                    onApprove={() => {}}
                    onDecline={(id) => handleDecline(id, "sponsor")}
                    loading={actionLoading === req.id}
                  />
                ))
              )
            )}

            {activeTab === "waitlist" && (
              waitlist.length === 0 ? (
                <p className="text-center text-stone-400 py-12 font-manrope">No waitlist signups yet</p>
              ) : (
                <div className="bg-white border border-stone-200">
                  <div className="grid grid-cols-3 gap-4 p-4 border-b border-stone-200 bg-stone-50">
                    <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Email</p>
                    <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Interest</p>
                    <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Date</p>
                  </div>
                  {waitlist.map((signup, index) => (
                    <div 
                      key={signup.id || index} 
                      className="grid grid-cols-3 gap-4 p-4 border-b border-stone-100 last:border-b-0 hover:bg-stone-50 transition-colors"
                    >
                      <p className="font-manrope text-sm text-charcoal">{signup.email}</p>
                      <p className="font-manrope text-sm text-stone-600">
                        {signup.interest || <span className="text-stone-400">All rooms</span>}
                      </p>
                      <p className="font-manrope text-xs text-stone-400">
                        {new Date(signup.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )
            )}

            {/* ATTENDEES TAB */}
            {activeTab === "attendees" && (
              <>
                {attendeeStats && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <div className="p-4 bg-white border border-stone-200">
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider mb-1">Total</p>
                      <p className="font-cormorant text-2xl text-charcoal">{attendeeStats.total}</p>
                    </div>
                    <div className="p-4 bg-white border border-stone-200">
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider mb-1">Founding Circle</p>
                      <p className="font-cormorant text-2xl text-gold-600">{attendeeStats.by_tier.founding_circle}</p>
                    </div>
                    <div className="p-4 bg-white border border-stone-200">
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider mb-1">Sovereign</p>
                      <p className="font-cormorant text-2xl text-charcoal">{attendeeStats.by_tier.sovereign_access}</p>
                    </div>
                    <div className="p-4 bg-white border border-stone-200">
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider mb-1">Room Access</p>
                      <p className="font-cormorant text-2xl text-charcoal">{attendeeStats.by_tier.room_access}</p>
                    </div>
                    <div className="p-4 bg-white border border-stone-200">
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider mb-1">Revenue</p>
                      <p className="font-cormorant text-2xl text-green-600">${attendeeStats.total_revenue?.toLocaleString()}</p>
                    </div>
                  </div>
                )}
                {attendees.length === 0 ? (
                  <p className="text-center text-stone-400 py-12 font-manrope">No ticket reservations yet</p>
                ) : (
                  <div className="bg-white border border-stone-200">
                    <div className="grid grid-cols-5 gap-4 p-4 border-b border-stone-200 bg-stone-50">
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Name</p>
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Email</p>
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Tier</p>
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Interests</p>
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Reserved</p>
                    </div>
                    {attendees.map((att) => (
                      <div 
                        key={att.id} 
                        className="grid grid-cols-5 gap-4 p-4 border-b border-stone-100 last:border-b-0 hover:bg-stone-50 transition-colors"
                      >
                        <p className="font-manrope text-sm text-charcoal">{att.full_name}</p>
                        <p className="font-manrope text-sm text-stone-600">{att.email}</p>
                        <p className={`font-manrope text-xs uppercase tracking-wider ${
                          att.ticket_tier === 'founding_circle' ? 'text-gold-600' : 'text-stone-600'
                        }`}>
                          {att.ticket_tier?.replace('_', ' ')}
                        </p>
                        <p className="font-manrope text-xs text-stone-500">
                          {att.poker_interest && <span className="mr-2">🎰 Poker</span>}
                          {att.streaming_interest && <span>📺 Stream</span>}
                        </p>
                        <p className="font-manrope text-xs text-stone-400">
                          {new Date(att.reserved_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* PARTNERS TAB */}
            {activeTab === "partners" && (
              partners.length === 0 ? (
                <p className="text-center text-stone-400 py-12 font-manrope">No partnership inquiries yet</p>
              ) : (
                <div className="bg-white border border-stone-200">
                  <div className="grid grid-cols-5 gap-4 p-4 border-b border-stone-200 bg-stone-50">
                    <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Company</p>
                    <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Contact</p>
                    <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Interests</p>
                    <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Budget</p>
                    <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Status</p>
                  </div>
                  {partners.map((partner) => (
                    <div 
                      key={partner.id} 
                      className="grid grid-cols-5 gap-4 p-4 border-b border-stone-100 last:border-b-0 hover:bg-stone-50 transition-colors"
                    >
                      <div>
                        <p className="font-manrope text-sm text-charcoal">{partner.company_name}</p>
                        <p className="font-manrope text-xs text-stone-500">{partner.email}</p>
                      </div>
                      <p className="font-manrope text-sm text-stone-600">{partner.contact_name}</p>
                      <p className="font-manrope text-xs text-stone-500">
                        {partner.partnership_interest?.slice(0, 2).join(', ')}
                        {partner.partnership_interest?.length > 2 && ` +${partner.partnership_interest.length - 2}`}
                      </p>
                      <p className="font-manrope text-sm text-stone-600">{partner.budget_range || '-'}</p>
                      <span className={`px-2 py-1 text-xs font-manrope uppercase tracking-wider inline-block ${
                        partner.status === 'confirmed' ? 'bg-green-50 text-green-700' :
                        partner.status === 'negotiating' ? 'bg-blue-50 text-blue-700' :
                        partner.status === 'contacted' ? 'bg-amber-50 text-amber-700' :
                        'bg-stone-100 text-stone-600'
                      }`}>
                        {partner.status}
                      </span>
                    </div>
                  ))}
                </div>
              )
            )}

            {/* DIRECT ACCESS LINKS TAB */}
            {activeTab === "access-links" && (
              <>
                {/* Create New Link Form */}
                <div className="bg-white border border-stone-200 p-6 mb-6">
                  <h3 className="font-manrope text-sm font-medium text-charcoal mb-4">Create Direct Access Link</h3>
                  <div className="grid md:grid-cols-4 gap-4">
                    <input
                      type="text"
                      placeholder="Full Name"
                      value={newLinkData.full_name}
                      onChange={(e) => setNewLinkData({...newLinkData, full_name: e.target.value})}
                      className="px-3 py-2 border border-stone-200 font-manrope text-sm"
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      value={newLinkData.email}
                      onChange={(e) => setNewLinkData({...newLinkData, email: e.target.value})}
                      className="px-3 py-2 border border-stone-200 font-manrope text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Note (optional)"
                      value={newLinkData.note}
                      onChange={(e) => setNewLinkData({...newLinkData, note: e.target.value})}
                      className="px-3 py-2 border border-stone-200 font-manrope text-sm"
                    />
                    <button
                      onClick={handleCreateAccessLink}
                      className="px-4 py-2 bg-charcoal text-white font-manrope text-xs uppercase tracking-wider hover:bg-gold-600 transition-colors"
                    >
                      Create Link
                    </button>
                  </div>
                  {createdLink && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200">
                      <p className="font-manrope text-sm text-green-800 mb-2">Link created for {createdLink.name}:</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 p-2 bg-white border text-sm break-all">{createdLink.access_url}</code>
                        <button 
                          onClick={() => copyToClipboard(createdLink.access_url)}
                          className="p-2 hover:bg-green-100 transition-colors"
                        >
                          <Copy className="w-4 h-4 text-green-700" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {accessLinks.length === 0 ? (
                  <p className="text-center text-stone-400 py-12 font-manrope">No direct access links created yet</p>
                ) : (
                  <div className="bg-white border border-stone-200">
                    <div className="grid grid-cols-5 gap-4 p-4 border-b border-stone-200 bg-stone-50">
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Name</p>
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Email</p>
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Note</p>
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Status</p>
                      <p className="font-manrope text-[10px] text-stone-500 uppercase tracking-wider">Link</p>
                    </div>
                    {accessLinks.map((link) => (
                      <div 
                        key={link.id} 
                        className="grid grid-cols-5 gap-4 p-4 border-b border-stone-100 last:border-b-0 hover:bg-stone-50 transition-colors"
                      >
                        <p className="font-manrope text-sm text-charcoal">{link.full_name}</p>
                        <p className="font-manrope text-sm text-stone-600">{link.email}</p>
                        <p className="font-manrope text-xs text-stone-500">{link.note || '-'}</p>
                        <span className={`px-2 py-1 text-xs font-manrope uppercase tracking-wider inline-block ${
                          link.used ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                        }`}>
                          {link.used ? 'Used' : 'Pending'}
                        </span>
                        <button 
                          onClick={() => copyToClipboard(`${window.location.origin}/access/${link.access_token}`)}
                          className="text-left font-manrope text-xs text-gold-600 hover:text-gold-700 flex items-center gap-1"
                        >
                          <Copy className="w-3 h-3" /> Copy Link
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </main>
  );
};

export default AdminDashboard;
